// cspell:ignore ntile
import * as z from "zod";
import {
  AnalyticsRelativeBound,
  AnalyticsRelativeFrameMode,
  AnalyticsRelativeNullOrdering,
  AnalyticsRelativeNullTreatment,
} from "../analytics-query/analytics-query-constants";
import { CustomAnalyticsQueryPlanCastTypeSchema } from "./custom-analytics-query-plan-primitives";

export const CUSTOM_ANALYTICS_QUERY_PLAN_VERSION = 1 as const;

export type CustomAnalyticsQueryPlanVersion =
  typeof CUSTOM_ANALYTICS_QUERY_PLAN_VERSION;

export const trimStringInput = (value: unknown) =>
  typeof value === "string" ? value.trim() : value;

const RawCustomAnalyticsQueryPlanIdentifierSchema = z
  .string()
  .regex(/^[a-zA-Z_][a-zA-Z0-9_]*$/)
  .max(64);

export const CustomAnalyticsQueryPlanIdentifierSchema = z.preprocess(
  trimStringInput,
  RawCustomAnalyticsQueryPlanIdentifierSchema,
);

export const CustomAnalyticsQueryPlanNameSchema = z.string().min(1).max(120);

/** Reserved: the default join site is spelled by ABSENCE, never by this token. */
export const CUSTOM_ANALYTICS_QUERY_PLAN_DEFAULT_OCCURRENCE = "default";

const RawCustomAnalyticsQueryPlanOccurrenceSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]*$/)
  .max(24)
  .refine((value) => value !== CUSTOM_ANALYTICS_QUERY_PLAN_DEFAULT_OCCURRENCE, {
    message: "Omit occurrence for a table's default join site.",
  });

/** Deliberately tighter than the identifier schema: this is an SQL alias suffix. */
export const CustomAnalyticsQueryPlanOccurrenceSchema = z.preprocess(
  trimStringInput,
  RawCustomAnalyticsQueryPlanOccurrenceSchema,
);

export const TrimmedCustomAnalyticsQueryPlanCastTypeSchema = z.preprocess(
  trimStringInput,
  CustomAnalyticsQueryPlanCastTypeSchema,
);

export const CustomAnalyticsQueryPlanAggregateFnSchema = z.enum([
  "count",
  "count_distinct",
  "count_if",
  "sum",
  "avg",
  "min",
  "max",
  "any_value",
  "array_agg_distinct",
  "booland_agg",
  "boolor_agg",
  "percentile_cont",
  "percentile_disc",
  "stddev_samp",
  "var_samp",
]);

export type CustomAnalyticsQueryPlanAggregateFn = z.infer<
  typeof CustomAnalyticsQueryPlanAggregateFnSchema
>;

export const CustomAnalyticsQueryPlanWindowOnlyFnSchema = z.enum([
  "cume_dist",
  "dense_rank",
  "first_value",
  "lag",
  "last_value",
  "lead",
  "nth_value",
  "ntile",
  "percent_rank",
  "rank",
  "row_number",
]);

export type CustomAnalyticsQueryPlanWindowOnlyFn = z.infer<
  typeof CustomAnalyticsQueryPlanWindowOnlyFnSchema
>;

export type CustomAnalyticsQueryPlanWindowFn =
  | CustomAnalyticsQueryPlanAggregateFn
  | CustomAnalyticsQueryPlanWindowOnlyFn;

export const CustomAnalyticsQueryPlanWindowFnSchema = z.enum([
  ...CustomAnalyticsQueryPlanAggregateFnSchema.options,
  ...CustomAnalyticsQueryPlanWindowOnlyFnSchema.options,
]);

export function isCustomAnalyticsQueryPlanWindowOnlyFn(
  fn: string | undefined,
): fn is CustomAnalyticsQueryPlanWindowOnlyFn {
  return CustomAnalyticsQueryPlanWindowOnlyFnSchema.safeParse(fn).success;
}

export function parseCustomAnalyticsQueryPlanWindowFn(
  fn: string | undefined,
): CustomAnalyticsQueryPlanWindowFn {
  const parsed = CustomAnalyticsQueryPlanWindowFnSchema.safeParse(fn);
  if (!parsed.success) {
    throw new Error(`Unsupported analytics window function: ${fn}.`);
  }
  return parsed.data;
}

export const CustomAnalyticsQueryPlanFrameModeSchema = z.enum(
  AnalyticsRelativeFrameMode,
);

export const CustomAnalyticsQueryPlanFrameBoundKindSchema = z.enum(
  AnalyticsRelativeBound,
);

export const CustomAnalyticsQueryPlanNullOrderingSchema = z.enum(
  AnalyticsRelativeNullOrdering,
);

export const CustomAnalyticsQueryPlanNullTreatmentSchema = z.enum(
  AnalyticsRelativeNullTreatment,
);

export const CUSTOM_ANALYTICS_QUERY_PLAN_WINDOW_ORDER_TERM_LIMIT = 30;

export const CUSTOM_ANALYTICS_QUERY_PLAN_WINDOW_PARTITION_TERM_LIMIT = 5;

export const CUSTOM_ANALYTICS_QUERY_PLAN_WINDOW_ARG_LIMIT = 3;

export const CUSTOM_ANALYTICS_QUERY_PLAN_WINDOW_HOIST_PREFIX = "__win";

export interface CustomAnalyticsQueryPlanAggregateSpec {
  readonly requiresInput: boolean;
  readonly distinctInput: boolean;
  readonly deterministicOrder: boolean;
  readonly parameter: "none" | "quantile";
}

export const CustomAnalyticsQueryPlanAggregateSpecs = {
  any_value: {
    requiresInput: true,
    distinctInput: false,
    deterministicOrder: false,
    parameter: "none",
  },
  array_agg_distinct: {
    requiresInput: true,
    distinctInput: true,
    deterministicOrder: true,
    parameter: "none",
  },
  avg: {
    requiresInput: true,
    distinctInput: false,
    deterministicOrder: false,
    parameter: "none",
  },
  booland_agg: {
    requiresInput: true,
    distinctInput: false,
    deterministicOrder: false,
    parameter: "none",
  },
  boolor_agg: {
    requiresInput: true,
    distinctInput: false,
    deterministicOrder: false,
    parameter: "none",
  },
  count: {
    requiresInput: false,
    distinctInput: false,
    deterministicOrder: false,
    parameter: "none",
  },
  count_distinct: {
    requiresInput: true,
    distinctInput: true,
    deterministicOrder: false,
    parameter: "none",
  },
  count_if: {
    requiresInput: true,
    distinctInput: false,
    deterministicOrder: false,
    parameter: "none",
  },
  max: {
    requiresInput: true,
    distinctInput: false,
    deterministicOrder: false,
    parameter: "none",
  },
  min: {
    requiresInput: true,
    distinctInput: false,
    deterministicOrder: false,
    parameter: "none",
  },
  percentile_cont: {
    requiresInput: true,
    distinctInput: false,
    deterministicOrder: false,
    parameter: "quantile",
  },
  percentile_disc: {
    requiresInput: true,
    distinctInput: false,
    deterministicOrder: false,
    parameter: "quantile",
  },
  stddev_samp: {
    requiresInput: true,
    distinctInput: false,
    deterministicOrder: false,
    parameter: "none",
  },
  sum: {
    requiresInput: true,
    distinctInput: false,
    deterministicOrder: false,
    parameter: "none",
  },
  var_samp: {
    requiresInput: true,
    distinctInput: false,
    deterministicOrder: false,
    parameter: "none",
  },
} as const satisfies Record<
  CustomAnalyticsQueryPlanAggregateFn,
  CustomAnalyticsQueryPlanAggregateSpec
>;

export function parseCustomAnalyticsQueryPlanAggregateFn(
  fn: string | undefined,
): CustomAnalyticsQueryPlanAggregateFn {
  const parsed = CustomAnalyticsQueryPlanAggregateFnSchema.safeParse(fn);
  if (!parsed.success) {
    throw new Error(`Unsupported analytics aggregate function: ${fn}.`);
  }
  return parsed.data;
}

export function findCustomAnalyticsQueryPlanAggregateSpec(
  fn: string | undefined,
): CustomAnalyticsQueryPlanAggregateSpec | undefined {
  const parsed = CustomAnalyticsQueryPlanAggregateFnSchema.safeParse(fn);
  return parsed.success
    ? CustomAnalyticsQueryPlanAggregateSpecs[parsed.data]
    : undefined;
}

export const CUSTOM_ANALYTICS_QUERY_PLAN_FINAL_OUTPUT_LIMIT = 25;
